import { DomainError } from '../errors.js';

export const BACKUP_POLICY_NAMES = ['standard', 'active', 'critical', 'custom'] as const;
export type BackupPolicyName = (typeof BACKUP_POLICY_NAMES)[number];

export const BACKUP_KINDS = ['automatic', 'manual', 'pre_deploy', 'pre_restore', 'pre_migrate', 'import'] as const;
export type BackupKind = (typeof BACKUP_KINDS)[number];

export const BACKUP_VERIFICATION_STATES = ['pending', 'verified', 'failed'] as const;
export type BackupVerificationState = (typeof BACKUP_VERIFICATION_STATES)[number];

/** The panel backs itself up daily: without a copy newer than a day and a half, it has missed at least one night. */
export const PANEL_BACKUP_LATE_MS = 36 * 3_600_000;

/** Whether the panel's own newest backup is late; `null` when there is none at all. */
export function panelBackupLate(takenAt: Date | null, now: Date): boolean {
  return takenAt === null || now.getTime() - takenAt.getTime() > PANEL_BACKUP_LATE_MS;
}

export interface RetentionRule {
  /** How often a backup is taken, in hours. */
  readonly intervalHours: number;
  readonly keepHourly: number;
  readonly keepDaily: number;
  readonly keepWeekly: number;
  readonly keepMonthly: number;
}

/** Policies from TZ section 11.4. */
export const BACKUP_POLICIES: Record<Exclude<BackupPolicyName, 'custom'>, RetentionRule> = {
  standard: { intervalHours: 24, keepHourly: 0, keepDaily: 14, keepWeekly: 8, keepMonthly: 12 },
  active: { intervalHours: 6, keepHourly: 0, keepDaily: 14, keepWeekly: 8, keepMonthly: 12 },
  critical: { intervalHours: 1, keepHourly: 24, keepDaily: 30, keepWeekly: 12, keepMonthly: 12 },
};

/** Recovery point objectives per policy (TZ section 11.7), in hours. */
export const POLICY_RPO_HOURS: Record<Exclude<BackupPolicyName, 'custom'>, number> = {
  standard: 24,
  active: 6,
  critical: 1,
};

export interface BackupRecord {
  readonly id: string;
  readonly createdAt: Date;
  readonly kind: BackupKind;
  readonly verification: BackupVerificationState;
}

export interface RetentionDecision {
  readonly keep: readonly string[];
  readonly prune: readonly string[];
}

function bucketKey(date: Date, granularity: 'hour' | 'day' | 'week' | 'month'): string {
  const year = date.getUTCFullYear();
  switch (granularity) {
    case 'hour':
      return `${year}-${date.getUTCMonth()}-${date.getUTCDate()}-${date.getUTCHours()}`;
    case 'day':
      return `${year}-${date.getUTCMonth()}-${date.getUTCDate()}`;
    case 'week': {
      // Days since epoch, floored to whole weeks. Calendar weeks are not needed here;
      // the only requirement is that backups seven days apart land in different buckets.
      const days = Math.floor(date.getTime() / 86_400_000);
      return `w${Math.floor(days / 7)}`;
    }
    case 'month':
      return `${year}-${date.getUTCMonth()}`;
  }
}

/**
 * Decides which backups survive retention.
 *
 * Two rules are invariants, not settings (TZ section 11.4):
 *
 *  1. Only `verified` backups can satisfy a retention slot. An unverified archive is
 *     not known to restore, so counting it would let a slot be "filled" by nothing.
 *  2. The newest verified backup is never pruned, whatever the rule says. Retention
 *     that can delete the last working copy is a deletion feature wearing a schedule.
 */
export function selectBackupsToPrune(
  backups: readonly BackupRecord[],
  rule: RetentionRule,
): RetentionDecision {
  const ordered = [...backups].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const keep = new Set<string>();

  const newestVerified = ordered.find((backup) => backup.verification === 'verified');
  if (newestVerified) keep.add(newestVerified.id);

  // A backup still being verified may become the newest verified copy in a moment.
  // Pruning it because it does not yet fill a slot would race the verifier.
  for (const backup of ordered) {
    if (backup.verification === 'pending') keep.add(backup.id);
  }

  const tiers: ReadonlyArray<[Parameters<typeof bucketKey>[1], number]> = [
    ['hour', rule.keepHourly],
    ['day', rule.keepDaily],
    ['week', rule.keepWeekly],
    ['month', rule.keepMonthly],
  ];

  for (const [granularity, count] of tiers) {
    if (count <= 0) continue;
    const seen = new Set<string>();
    for (const backup of ordered) {
      if (backup.verification !== 'verified') continue;
      const bucket = bucketKey(backup.createdAt, granularity);
      if (seen.has(bucket)) continue;
      seen.add(bucket);
      keep.add(backup.id);
      if (seen.size >= count) break;
    }
  }

  return {
    keep: ordered.filter((backup) => keep.has(backup.id)).map((backup) => backup.id),
    prune: ordered.filter((backup) => !keep.has(backup.id)).map((backup) => backup.id),
  };
}

/**
 * Guards an explicit delete. Retention runs through `selectBackupsToPrune`, but an
 * operator deleting by hand reaches this path, and the invariant must hold there too.
 */
export function assertDeletable(target: BackupRecord, all: readonly BackupRecord[]): void {
  if (target.verification !== 'verified') return;

  const otherVerified = all.some(
    (backup) => backup.id !== target.id && backup.verification === 'verified',
  );
  if (!otherVerified) {
    throw new DomainError(
      'LAST_VERIFIED_BACKUP_PROTECTED',
      'Refusing to delete the only verified backup of this site',
      { backupId: target.id },
    );
  }
}

/** True when the newest verified backup is older than the policy allows (TZ section 11.7). */
export function isBackupFreshnessBreached(
  backups: readonly BackupRecord[],
  rpoHours: number,
  now: Date,
): boolean {
  const newestVerified = backups
    .filter((backup) => backup.verification === 'verified')
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];

  if (!newestVerified) return true;
  return now.getTime() - newestVerified.createdAt.getTime() > rpoHours * 3_600_000;
}

/**
 * Copies kept per site (ADR-0056): the owner's cap, set on the Backups page.
 * With it, retention keeps the newest verified copies and nothing older — a
 * new copy takes the place of the oldest once it is verified, never before.
 */
export const BACKUP_COPIES_DEFAULT = 3;
export const BACKUP_COPIES_MAX = 10;

export interface BackupSettings {
  /** Copies a site keeps; each new verified one replaces the oldest. */
  readonly copiesPerSite: number;
}

export const DEFAULT_BACKUP_SETTINGS: BackupSettings = { copiesPerSite: BACKUP_COPIES_DEFAULT };

/**
 * Retention by count (ADR-0056): the `copies` newest verified backups stay,
 * with the ones still being verified; the rest go. The two invariants of
 * `selectBackupsToPrune` hold here too — only verified copies fill a place,
 * and the newest verified is always among them.
 */
export function selectLatestCopies(backups: readonly BackupRecord[], copies: number): RetentionDecision {
  const ordered = [...backups].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const keep = new Set<string>();
  for (const backup of ordered) if (backup.verification === 'pending') keep.add(backup.id);
  for (const backup of ordered.filter((item) => item.verification === 'verified').slice(0, Math.max(1, copies))) keep.add(backup.id);
  return {
    keep: ordered.filter((backup) => keep.has(backup.id)).map((backup) => backup.id),
    prune: ordered.filter((backup) => !keep.has(backup.id)).map((backup) => backup.id),
  };
}

/**
 * Which slot each kept copy stands in (ADR-0056): «Копия 1», «Копия 2»… A
 * copy keeps its number for life; a new one takes the number of the oldest it
 * replaced — the owner's «копия 4 перезаписывает копию 1» — or, while there is
 * room, the lowest number free. Answers every kept verified copy's number from
 * now on; null for one beyond the cap (a safety copy before a change, or one
 * left over when the cap was lowered).
 */
export function assignSlots(
  kept: readonly { readonly id: string; readonly createdAt: Date; readonly verification: BackupVerificationState; readonly slot: number | null }[],
  pruned: readonly { readonly slot: number | null }[],
  copies: number,
): Map<string, number | null> {
  const valid = (slot: number | null): slot is number => slot !== null && Number.isInteger(slot) && slot >= 1 && slot <= copies;
  const verified = kept.filter((backup) => backup.verification === 'verified').sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  // Who holds each number now: the older copy, when two claim the same one.
  const owner = new Map<number, string>();
  for (const backup of verified) if (valid(backup.slot) && !owner.has(backup.slot)) owner.set(backup.slot, backup.id);
  const result = new Map<string, number | null>();
  for (const [slot, id] of owner) result.set(id, slot);
  // Freed numbers first, in the order they were freed; then the lowest free ones.
  const free = [...new Set([...pruned.map((backup) => backup.slot).filter(valid), ...Array.from({ length: copies }, (_, index) => index + 1)])].filter(
    (slot) => !owner.has(slot),
  );
  for (const backup of verified) {
    if (result.has(backup.id)) continue;
    result.set(backup.id, free.shift() ?? null);
  }
  return result;
}

/**
 * What is wrong with a site's copies, if anything (ADR-0056), for the Backups
 * page to point at: no connected server to take them from, the newest failed,
 * none verified yet, or the newest verified older than its policy allows with
 * half again for a late night.
 */
export function backupProblem(
  input: {
    readonly hasServer: boolean;
    readonly policy: string | null;
    readonly copies: readonly { readonly verification: string; readonly createdAt: Date }[];
  },
  now: Date,
): 'failed' | 'none' | 'stale' | 'unprotected' | null {
  if (!input.hasServer) return 'unprotected';
  const ordered = [...input.copies].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const newest = ordered[0];
  if (!newest) return 'none';
  if (newest.verification === 'failed') return 'failed';
  const verified = ordered.find((copy) => copy.verification === 'verified');
  if (!verified) return newest.verification === 'pending' ? null : 'none';
  const rpo = POLICY_RPO_HOURS[input.policy as keyof typeof POLICY_RPO_HOURS] ?? 24;
  return now.getTime() - verified.createdAt.getTime() > rpo * 1.5 * 3_600_000 ? 'stale' : null;
}
